"use client"

import { useState, useEffect } from "react"
import { Text, Button } from "@medusajs/ui"
import { ChevronDown, ChevronUpMini } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import type { SearchFilters } from "../../templates/search-template"
import { getHierarchicalCategories } from "@/lib/util/category-helpers"
import { getAvailablePriceRange } from "@/lib/data/search"
import PriceRangeSlider from "../price-range-slider"

type FilterOption = {
  value: string
  count: number
}

type FilterOptions = {
  brands: FilterOption[]
  subcategories: FilterOption[]
  packaging: FilterOption[]
  orderUnit: FilterOption[]
  region: FilterOption[]
  category: FilterOption[]
  categories: FilterOption[]
  type: FilterOption[]
  color: FilterOption[]
  voltage: FilterOption[]
  volume: FilterOption[]
  ipProtection: FilterOption[]
  connectivity: FilterOption[]
  gnSize: FilterOption[]
  material: FilterOption[]
  mainMaterial: FilterOption[]
  zmogljivost: FilterOption[]
  proizvodnaZmogljivost: FilterOption[]
  klimatskiRazdred: FilterOption[]
  izdelekZlozljiv: FilterOption[]
  drzavaPorekla: FilterOption[]
  energiskiRazred: FilterOption[]
  primeranZaUporabo: FilterOption[]
  hladilnoSredstvo: FilterOption[]
  odtocnaPipa: FilterOption[]
  steviloVentilatorjev: FilterOption[]
  debelinaRezanja: FilterOption[]
  tezaBaterije: FilterOption[]
  dolzinaKabla: FilterOption[]
  montazoPotrebenElektricar: FilterOption[]
  mocPlinskegaGorilnika: FilterOption[]
  tlakPlina: FilterOption[]
  stopnjaRegulacije: FilterOption[]
  enotaZaRegulacije: FilterOption[]
  zaPriklopVodovodar: FilterOption[]
  osnovniPremer: FilterOption[]
  dolzinaRezila: FilterOption[]
  casPripraveVode: FilterOption[]
  tezaVsebine: FilterOption[]
  brutoTeza: FilterOption[]
  netoTeza: FilterOption[]
  dolzinaRocaja: FilterOption[]
  premer: FilterOption[]
  sirina: FilterOption[]
  dolzina: FilterOption[]
  visina: FilterOption[]
  notranjaSirina: FilterOption[]
  notranjaGlobina: FilterOption[]
  notranjaVisina: FilterOption[]
  vhodnaMoc: FilterOption[]
  izhodnaMoc: FilterOption[]
  najvecjiVolumen: FilterOption[]
  najvecjaSirinaZivila: FilterOption[]
  vrtljajevNaMinuto: FilterOption[]
  dolzinaGredi: FilterOption[]
  debelinaIzolacije: FilterOption[]
  temperaturaOd: FilterOption[]
  temperaturaDo: FilterOption[]
  // New attributes
  debelinaMaterial: FilterOption[]
  odporenNaTempOd: FilterOption[]
  odporenNaTempDo: FilterOption[]
  premerZgoraj: FilterOption[]
  merePredala: FilterOption[]
  dolzinaXSirina: FilterOption[]
  premerSpodaj: FilterOption[]
  sifraIzdelka: FilterOption[]
  ean: FilterOption[]
  zmogljivostCrpalke: FilterOption[]
  vrstaIzdelka: FilterOption[]
  prostorninaVMl: FilterOption[]
  dolzinaRogljev: FilterOption[]
  dolzinaXSirinaXVisina: FilterOption[]
  dolzinaXVisina: FilterOption[]
  zunanjaDelzinaXSirinaXVisina: FilterOption[]
  notranjaDelzinaXSirinaXVisina: FilterOption[]
  velikostZajemalke: FilterOption[]
  motiv: FilterOption[]
  casomer: FilterOption[]
  enotaCasomera: FilterOption[]
  razmikMedVodiliPladnjev: FilterOption[]
  uporabnaProstornina: FilterOption[]
  vrstaKontrolnePlosce: FilterOption[]
  tehtanjeOd: FilterOption[]
  tehtanjeDo: FilterOption[]
  izdelekJePotrebnoSestaviti: FilterOption[]
  vsebujeBaterijo: FilterOption[]
  steviloBaterij: FilterOption[]
  izdelekJeTekoc: FilterOption[]
  brand: FilterOption[]
}

// All filters that are simple string arrays
export type ArrayFilterKeys = keyof Omit<SearchFilters, 
  | 'categories' 
  | 'priceRange' 
  | 'inStock' 
  | 'fastDelivery' 
  | 'bulkPricing'
  | 'programmable'
>

// Map keys to their display titles
export const FILTER_TITLE_MAP: Record<ArrayFilterKeys, string> = {
  brands: 'Blagovne znamke',
  subcategories: 'Podkategorije',
  packaging: 'Embalaža',
  orderUnit: 'Enota naročila',
  region: 'Regija',
  category: 'Kategorija',
  type: 'Tip izdelka',
  color: 'Barva',
  voltage: 'Napetost',
  volume: 'Prostornina (L)',
  ipProtection: 'IP zaščita',
  connectivity: 'Povezljivost',
  gnSize: 'GN velikost',
  material: 'Material',
  mainMaterial: 'Glavni material',
  zmogljivost: 'Zmogljivost',
  proizvodnaZmogljivost: 'Proizvodna zmogljivost',
  klimatskiRazdred: 'Klimatski razred',
  izdelekZlozljiv: 'Izdelek zložljiv',
  drzavaPorekla: 'Država porekla',
  energiskiRazred: 'Energijski razred',
  primeranZaUporabo: 'Primeran za uporabo',
  hladilnoSredstvo: 'Hladilno sredstvo',
  odtocnaPipa: 'Odtočna pipa',
  steviloVentilatorjev: 'Število ventilatorjev',
  debelinaRezanja: 'Debelina rezanja',
  tezaBaterije: 'Teža baterije',
  dolzinaKabla: 'Dolžina kabla',
  montazoPotrebenElektricar: 'Montažo potreben električar',
  mocPlinskegaGorilnika: 'Moč plinskega gorilnika',
  tlakPlina: 'Tlak plina',
  stopnjaRegulacije: 'Stopnja regulacije',
  enotaZaRegulacije: 'Enota za regulacije',
  zaPriklopVodovodar: 'Za priklop vodovodar',
  osnovniPremer: 'Osnovni premer (cm)',
  dolzinaRezila: 'Dolžina rezila (cm)',
  casPripraveVode: 'Čas priprave vode (min)',
  tezaVsebine: 'Teža vsebine (kg)',
  brutoTeza: 'Bruto teža (kg)',
  netoTeza: 'Neto teža (kg)',
  dolzinaRocaja: 'Dolžina ročaja (cm)',
  premer: 'Premer (cm)',
  sirina: 'Širina (cm)',
  dolzina: 'Dolžina (cm)',
  visina: 'Višina (cm)',
  notranjaSirina: 'Notranja širina (cm)',
  notranjaGlobina: 'Notranja globina (cm)',
  notranjaVisina: 'Notranja višina (cm)',
  vhodnaMoc: 'Vhodna moč (W)',
  izhodnaMoc: 'Izhodna moč (W)',
  najvecjiVolumen: 'Največji volumen (L)',
  najvecjaSirinaZivila: 'Največja širina živila (cm)',
  vrtljajevNaMinuto: 'Vrtljajev na minuto',
  dolzinaGredi: 'Dolžina gredi (cm)',
  debelinaIzolacije: 'Debelina izolacije (mm)',
  temperaturaOd: 'Temperatura od',
  temperaturaDo: 'Temperatura do',
  // New filter titles
  debelinaMaterial: 'Debelina material (mm)',
  odporenNaTempOd: 'Odporen na temp. od',
  odporenNaTempDo: 'Odporen na temp. do',
  premerZgoraj: 'Premer zgoraj (cm)',
  merePredala: 'Mere predala (cm)',
  dolzinaXSirina: 'Dolžina x širina (cm)',
  premerSpodaj: 'Premer spodaj (cm)',
  sifraIzdelka: 'Šifra izdelka',
  ean: 'EAN',
  zmogljivostCrpalke: 'Zmogljivost črpalke',
  vrstaIzdelka: 'Vrsta izdelka',
  prostorninaVMl: 'Prostornina (ml)',
  dolzinaRogljev: 'Dolžina rogljev',
  dolzinaXSirinaXVisina: 'Dolžina x širina x višina (cm)',
  dolzinaXVisina: 'Dolžina x višina (cm)',
  zunanjaDelzinaXSirinaXVisina: 'Zunanja dolžina x širina x višina (cm)',
  notranjaDelzinaXSirinaXVisina: 'Notranja dolžina x širina x višina (cm)',
  velikostZajemalke: 'Velikost zajemalke (cm)',
  motiv: 'Motiv',
  casomer: 'Časomer',
  enotaCasomera: 'Enota časomera',
  razmikMedVodiliPladnjev: 'Razmik med vodili pladnjev',
  uporabnaProstornina: 'Uporabna prostornina',
  vrstaKontrolnePlosce: 'Vrsta kontrolne plošče',
  tehtanjeOd: 'Tehtanje od',
  tehtanjeDo: 'Tehtanje do',
  izdelekJePotrebnoSestaviti: 'Izdelek je potrebno sestaviti',
  vsebujeBaterijo: 'Vsebuje baterijo',
  steviloBaterij: 'Število baterij',
  izdelekJeTekoc: 'Izdelek je tekoč',
  brand: 'Blagovna znamka',
}

type SearchFiltersProps = {
  filters: SearchFilters
  onFiltersChange: (filters: Partial<SearchFilters>) => void
  categories: HttpTypes.StoreProductCategory[]
  facets?: Record<string, Record<string, number>>
  initialFilterOptions?: FilterOptions
  filterOptions?: FilterOptions
  loading?: boolean
  showCategoriesFilter?: boolean
}

export default function SearchFilters({
  filters,
  onFiltersChange,
  categories = [],
  facets = {},
  initialFilterOptions = {} as FilterOptions,
  filterOptions = {} as FilterOptions,
  loading = false,
  showCategoriesFilter = true,
}: SearchFiltersProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    categories: false,
    price: false,
    brands: false,
    materials: false,
    mainCategories: false,
    countries: false,
    subcategories: false,
    height: false,
    width: false,
    length: false,
    packaging: false,
    netWeight: false,
    grossWeight: false,
    orderUnit: false,
    region: false,
    category: false,
    color: false,
    energyClass: false,
    voltage: false,
    inputPower: false,
    volume: false,
    ipProtection: false,
    connectivity: false,
    gnSize: false,
    material: false,
    mainMaterial: false,
    zmogljivost: false,
    proizvodnaZmogljivost: false,
    klimatskiRazdred: false,
    izdelekZlozljiv: false,
    drzavaPorekla: false,
    energiskiRazred: false,
    primeranZaUporabo: false,
    hladilnoSredstvo: false,
    odtocnaPipa: false,
    steviloVentilatorjev: false,
    debelinaRezanja: false,
    tezaBaterije: false,
    dolzinaKabla: false,
    montazoPotrebenElektricar: false,
    mocPlinskegaGorilnika: false,
    tlakPlina: false,
    stopnjaRegulacije: false,
    enotaZaRegulacije: false,
    zaPriklopVodovodar: false,
    osnovniPremer: false,
    dolzinaRezila: false,
    casPripraveVode: false,
    tezaVsebine: false,
    brutoTeza: false,
    netoTeza: false,
    dolzinaRocaja: false,
    premer: false,
    sirina: false,
    dolzina: false,
    visina: false,
    notranjaSirina: false,
    notranjaGlobina: false,
    notranjaVisina: false,
    izhodnaMoc: false,
    najvecjiVolumen: false,
    najvecjaSirinaZivila: false,
    vrtljajevNaMinuto: false,
    dolzinaGredi: false,
    debelinaIzolacije: false,
    temperaturaOd: false,
    temperaturaDo: false,
    // New attributes
    debelinaMaterial: false,
    odporenNaTempOd: false,
    odporenNaTempDo: false,
    premerZgoraj: false,
    merePredala: false,
    dolzinaXSirina: false,
    premerSpodaj: false,
    sifraIzdelka: false,
    ean: false,
    zmogljivostCrpalke: false,
    vrstaIzdelka: false,
    prostorninaVMl: false,
    dolzinaRogljev: false,
    dolzinaXSirinaXVisina: false,
    dolzinaXVisina: false,
    zunanjaDelzinaXSirinaXVisina: false,
    notranjaDelzinaXSirinaXVisina: false,
    velikostZajemalke: false,
    motiv: false,
    casomer: false,
    enotaCasomera: false,
    razmikMedVodiliPladnjev: false,
    uporabnaProstornina: false,
    vrstaKontrolnePlosce: false,
    tehtanjeOd: false,
    tehtanjeDo: false,
    izdelekJePotrebnoSestaviti: false,
    vsebujeBaterijo: false,
    steviloBaterij: false,
    izdelekJeTekoc: false,
    brand: false,
  })
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [availablePriceRange, setAvailablePriceRange] = useState({ min: 0, max: 10000 })
  const [priceRangeLoading, setPriceRangeLoading] = useState(false)

  // Fetch available price range when component mounts or filters change
  useEffect(() => {
    const fetchPriceRange = async () => {
      setPriceRangeLoading(true)
      try {
        const range = await getAvailablePriceRange({
          categories: filters.categories,
          brands: filters.brands,
          subcategories: filters.subcategories,
          packaging: filters.packaging,
          orderUnit: filters.orderUnit,
          region: filters.region,
          category: filters.category,
          type: filters.type,
          inStock: filters.inStock,
          fastDelivery: filters.fastDelivery,
          bulkPricing: filters.bulkPricing,
          programmable: filters.programmable,
          color: filters.color,
          voltage: filters.voltage,
          volume: filters.volume,
          ipProtection: filters.ipProtection,
          connectivity: filters.connectivity,
          gnSize: filters.gnSize,
          material: filters.material,
          mainMaterial: filters.mainMaterial,
          zmogljivost: filters.zmogljivost,
          proizvodnaZmogljivost: filters.proizvodnaZmogljivost,
          klimatskiRazdred: filters.klimatskiRazdred,
          izdelekZlozljiv: filters.izdelekZlozljiv,
          drzavaPorekla: filters.drzavaPorekla,
          energiskiRazred: filters.energiskiRazred,
          primeranZaUporabo: filters.primeranZaUporabo,
          hladilnoSredstvo: filters.hladilnoSredstvo,
          odtocnaPipa: filters.odtocnaPipa,
          steviloVentilatorjev: filters.steviloVentilatorjev,
          debelinaRezanja: filters.debelinaRezanja,
          tezaBaterije: filters.tezaBaterije,
          dolzinaKabla: filters.dolzinaKabla,
          montazoPotrebenElektricar: filters.montazoPotrebenElektricar,
          mocPlinskegaGorilnika: filters.mocPlinskegaGorilnika,
          tlakPlina: filters.tlakPlina,
          stopnjaRegulacije: filters.stopnjaRegulacije,
          enotaZaRegulacije: filters.enotaZaRegulacije,
          zaPriklopVodovodar: filters.zaPriklopVodovodar,
          osnovniPremer: filters.osnovniPremer,
          dolzinaRezila: filters.dolzinaRezila,
          casPripraveVode: filters.casPripraveVode,
          tezaVsebine: filters.tezaVsebine,
          brutoTeza: filters.brutoTeza,
          netoTeza: filters.netoTeza,
          dolzinaRocaja: filters.dolzinaRocaja,
          premer: filters.premer,
          sirina: filters.sirina,
          dolzina: filters.dolzina,
          visina: filters.visina,
          notranjaSirina: filters.notranjaSirina,
          notranjaGlobina: filters.notranjaGlobina,
          notranjaVisina: filters.notranjaVisina,
          vhodnaMoc: filters.vhodnaMoc,
          izhodnaMoc: filters.izhodnaMoc,
          najvecjiVolumen: filters.najvecjiVolumen,
          najvecjaSirinaZivila: filters.najvecjaSirinaZivila,
          vrtljajevNaMinuto: filters.vrtljajevNaMinuto,
          dolzinaGredi: filters.dolzinaGredi,
          debelinaIzolacije: filters.debelinaIzolacije,
          temperaturaOd: filters.temperaturaOd,
          temperaturaDo: filters.temperaturaDo,
          // New attributes
          debelinaMaterial: filters.debelinaMaterial,
          odporenNaTempOd: filters.odporenNaTempOd,
          odporenNaTempDo: filters.odporenNaTempDo,
          premerZgoraj: filters.premerZgoraj,
          merePredala: filters.merePredala,
          dolzinaXSirina: filters.dolzinaXSirina,
          premerSpodaj: filters.premerSpodaj,
          sifraIzdelka: filters.sifraIzdelka,
          ean: filters.ean,
          zmogljivostCrpalke: filters.zmogljivostCrpalke,
          vrstaIzdelka: filters.vrstaIzdelka,
          prostorninaVMl: filters.prostorninaVMl,
          dolzinaRogljev: filters.dolzinaRogljev,
          dolzinaXSirinaXVisina: filters.dolzinaXSirinaXVisina,
          dolzinaXVisina: filters.dolzinaXVisina,
          zunanjaDelzinaXSirinaXVisina: filters.zunanjaDelzinaXSirinaXVisina,
          notranjaDelzinaXSirinaXVisina: filters.notranjaDelzinaXSirinaXVisina,
          velikostZajemalke: filters.velikostZajemalke,
          motiv: filters.motiv,
          casomer: filters.casomer,
          enotaCasomera: filters.enotaCasomera,
          razmikMedVodiliPladnjev: filters.razmikMedVodiliPladnjev,
          uporabnaProstornina: filters.uporabnaProstornina,
          vrstaKontrolnePlosce: filters.vrstaKontrolnePlosce,
          tehtanjeOd: filters.tehtanjeOd,
          tehtanjeDo: filters.tehtanjeDo,
          izdelekJePotrebnoSestaviti: filters.izdelekJePotrebnoSestaviti,
          vsebujeBaterijo: filters.vsebujeBaterijo,
          steviloBaterij: filters.steviloBaterij,
          izdelekJeTekoc: filters.izdelekJeTekoc,
          brand: filters.brand,
        })
        setAvailablePriceRange(range)
      } catch (error) {
        // Handle error silently
      } finally {
        setPriceRangeLoading(false)
      }
    }

    fetchPriceRange()
  }, [filters])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCategoryChange = (categoryId: string, checked: boolean) => {
    const newCategories = checked
      ? [...filters.categories, categoryId]
      : filters.categories.filter(id => id !== categoryId)
    
    onFiltersChange({ categories: newCategories })
  }

  const handleFilterChange = (
    filterType: ArrayFilterKeys,
    value: string,
    checked: boolean
  ) => {
    const currentValues = filters[filterType] || []
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value)
    
    onFiltersChange({ [filterType]: newValues })
  }

  const handlePriceRangeChange = (min: number, max: number) => {
    onFiltersChange({ priceRange: [min, max] })
  }

  const handleQuickFilterChange = (filterType: keyof Pick<SearchFilters, 'inStock' | 'fastDelivery' | 'bulkPricing' | 'programmable'>, value: boolean) => {
    onFiltersChange({ [filterType]: value })
  }

  // Render filter section with dynamic options
  const renderFilterSection = (
    sectionKey: ArrayFilterKeys,
    maxHeight = "max-h-48"
  ) => {
    const initialOptions = (initialFilterOptions?.[sectionKey] || []) as FilterOption[]
    const currentOptions = (filterOptions?.[sectionKey] || []) as FilterOption[]
    const title = FILTER_TITLE_MAP[sectionKey]

    // Only show sections with at least one filter option
    if (!currentOptions?.length) return null

    return (
      <div key={sectionKey}>
        <button
          onClick={() => toggleSection(sectionKey)}
          className="flex items-center justify-between w-full py-2 text-left"
          disabled={loading}
        >
          <Text className="font-medium text-neutral-900">{title}</Text>
          {expandedSections[sectionKey] ? (
            <ChevronUpMini className="w-4 h-4 text-neutral-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-500" />
          )}
        </button>
        
        {expandedSections[sectionKey] && (
          <div className={`mt-3 space-y-2 ${maxHeight} overflow-y-auto`}>
            {currentOptions.map((option: FilterOption) => {
              // Use the initial count from initialFilterOptions, fallback to current count
              const initialOption = initialOptions.find((o: FilterOption) => o.value === option.value)
              const currentOption = currentOptions.find((o: FilterOption) => o.value === option.value)
              const count = initialOption?.count || currentOption?.count || 0
              const isSelected = (filters[sectionKey] || []).includes(option.value)
              
              return (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer hover:bg-neutral-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleFilterChange(sectionKey, option.value, e.target.checked)}
                    className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <Text className="text-sm text-neutral-700">{option.value}</Text>
                  <Text className="text-xs text-neutral-500 ml-auto">
                    ({count})
                  </Text>
                </label>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <Text className="font-semibold text-lg text-neutral-900">Filtri</Text>
        {loading && <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>}
      </div>

      <div className="space-y-6">
        {/* Quick Filters - Moved to top */}
        <div>
          <button
            onClick={() => toggleSection('quick')}
            className="flex items-center justify-between w-full py-2 text-left"
            disabled={loading}
          >
            <Text className="font-medium text-neutral-900">Hitri filtri</Text>
            {expandedSections.quick ? (
              <ChevronUpMini className="w-4 h-4 text-neutral-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-neutral-500" />
            )}
          </button>
          
          {expandedSections.quick && (
            <div className="mt-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.fastDelivery}
                  onChange={(e) => handleQuickFilterChange('fastDelivery', e.target.checked)}
                  className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                  disabled={loading}
                />
                <Text className="text-sm text-neutral-700">Samo na zalogi</Text>
              </label>
            </div>
          )}
        </div>

        {/* Categories */}
        {showCategoriesFilter && (
          <div>
            <button
              onClick={() => toggleSection('categories')}
              className="flex items-center justify-between w-full py-2 text-left"
              disabled={loading}
            >
              <Text className="font-medium text-neutral-900">Kategorije</Text>
              {expandedSections.categories ? (
                <ChevronUpMini className="w-4 h-4 text-neutral-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-neutral-500" />
              )}
            </button>

            {expandedSections.categories && (
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                {categories.length > 0 ? (
                  (() => {
                    const { mainCategories, subcategoriesByParent } = getHierarchicalCategories(categories)
                    return mainCategories.map((mainCategory) => {
                      const hasSubcategories = subcategoriesByParent[mainCategory.id]?.length > 0;
                      const isExpanded = expandedCategories[mainCategory.id];
                      return (
                      <div key={`main-${mainCategory.id}`} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 cursor-pointer hover:bg-neutral-50 p-1 rounded font-medium flex-grow">
                            <input
                              type="checkbox"
                              checked={filters.categories.includes(mainCategory.id)}
                              onChange={(e) =>
                                handleCategoryChange(mainCategory.id, e.target.checked)
                              }
                              className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                              disabled={loading}
                            />
                            <Text className="text-sm text-neutral-800 font-medium">
                              {mainCategory.name}
                            </Text>
                          </label>
                          {hasSubcategories && (
                            <button onClick={() => toggleCategoryExpansion(mainCategory.id)} className="p-1">
                              {isExpanded ? <ChevronUpMini className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                        
                        {hasSubcategories && isExpanded && (
                          <div className="ml-6 space-y-1 border-l border-neutral-200 pl-3">
                            {subcategoriesByParent[mainCategory.id].map((subcategory) => (
                              <label
                                key={`sub-${subcategory.id}`}
                                className="flex items-center gap-2 cursor-pointer hover:bg-neutral-50 p-1 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={filters.categories.includes(subcategory.id)}
                                  onChange={(e) =>
                                    handleCategoryChange(subcategory.id, e.target.checked)
                                  }
                                  className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                                  disabled={loading}
                                />
                                <Text className="text-sm text-neutral-600">
                                  {subcategory.name}
                                </Text>
                                {(facets['categories.id']?.[subcategory.id] ||
                                  facets['categories.handle']?.[subcategory.handle]) && (
                                  <Text className="text-xs text-neutral-500 ml-auto">
                                    (
                                    {facets['categories.id']?.[subcategory.id] ||
                                      facets['categories.handle']?.[subcategory.handle]}
                                    )
                                  </Text>
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )})
                  })()
                ) : (
                  <Text className="text-sm text-neutral-500 italic">
                    {loading
                      ? 'Nalaganje kategorij...'
                      : 'Ni razpoložljivih kategorij'}
                  </Text>
                )}
              </div>
            )}
          </div>
        )}

        {/* Price Range */}
        <div>
          <button
            onClick={() => toggleSection('price')}
            className="flex items-center justify-between w-full py-2 text-left"
            disabled={loading}
          >
            <Text className="font-medium text-neutral-900">Cenovni razpon</Text>
            {expandedSections.price ? (
              <ChevronUpMini className="w-4 h-4 text-neutral-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-neutral-500" />
            )}
          </button>
          
          {expandedSections.price && (
            <div className="mt-3 space-y-4">
              <div className="px-3">
                <PriceRangeSlider
                  minPrice={availablePriceRange.min}
                  maxPrice={availablePriceRange.max}
                  currentMin={filters.priceRange[0]}
                  currentMax={filters.priceRange[1]}
                  onRangeChange={handlePriceRangeChange}
                  loading={loading || priceRangeLoading}
                  disabled={loading || priceRangeLoading}
                />
              </div>
            </div>
          )}
        </div>

        {/* Dynamic Filter Sections */}
        {Object.keys(FILTER_TITLE_MAP).map((key) => 
          renderFilterSection(key as ArrayFilterKeys)
        )}
      </div>

      {/* Clear All Filters */}
      <div className="pt-6 mt-6 border-t border-neutral-200">
        <Button
          variant="secondary"
          className="w-full"
          onClick={() => onFiltersChange({
            categories: [],
            brands: [],
            subcategories: [],
            packaging: [],
            orderUnit: [],
            region: [],
            category: [],
            type: [],
            priceRange: [0, 10000],
            inStock: false,
            fastDelivery: false,
            bulkPricing: false,
            programmable: false,
            color: [],
            voltage: [],
            volume: [],
            ipProtection: [],
            connectivity: [],
            gnSize: [],
            material: [],
            mainMaterial: [],
            zmogljivost: [],
            proizvodnaZmogljivost: [],
            klimatskiRazdred: [],
            izdelekZlozljiv: [],
            drzavaPorekla: [],
            energiskiRazred: [],
            primeranZaUporabo: [],
            hladilnoSredstvo: [],
            odtocnaPipa: [],
            steviloVentilatorjev: [],
            debelinaRezanja: [],
            tezaBaterije: [],
            dolzinaKabla: [],
            montazoPotrebenElektricar: [],
            mocPlinskegaGorilnika: [],
            tlakPlina: [],
            stopnjaRegulacije: [],
            enotaZaRegulacije: [],
            zaPriklopVodovodar: [],
            osnovniPremer: [],
            dolzinaRezila: [],
            casPripraveVode: [],
            tezaVsebine: [],
            brutoTeza: [],
            netoTeza: [],
            dolzinaRocaja: [],
            premer: [],
            sirina: [],
            dolzina: [],
            visina: [],
            notranjaSirina: [],
            notranjaGlobina: [],
            notranjaVisina: [],
            vhodnaMoc: [],
            izhodnaMoc: [],
            najvecjiVolumen: [],
            najvecjaSirinaZivila: [],
            vrtljajevNaMinuto: [],
            dolzinaGredi: [],
            debelinaIzolacije: [],
            temperaturaOd: [],
            temperaturaDo: [],
            // New attributes
            debelinaMaterial: [],
            odporenNaTempOd: [],
            odporenNaTempDo: [],
            premerZgoraj: [],
            merePredala: [],
            dolzinaXSirina: [],
            premerSpodaj: [],
            sifraIzdelka: [],
            ean: [],
            zmogljivostCrpalke: [],
            vrstaIzdelka: [],
            prostorninaVMl: [],
            dolzinaRogljev: [],
            dolzinaXSirinaXVisina: [],
            dolzinaXVisina: [],
            zunanjaDelzinaXSirinaXVisina: [],
            notranjaDelzinaXSirinaXVisina: [],
            velikostZajemalke: [],
            motiv: [],
            casomer: [],
            enotaCasomera: [],
            razmikMedVodiliPladnjev: [],
            uporabnaProstornina: [],
            vrstaKontrolnePlosce: [],
            tehtanjeOd: [],
            tehtanjeDo: [],
            izdelekJePotrebnoSestaviti: [],
            vsebujeBaterijo: [],
            steviloBaterij: [],
            izdelekJeTekoc: [],
            brand: [],
          })}
          disabled={loading}
        >
          Počisti vse filtre
        </Button>
      </div>
    </div>
  )
} 